import { fromZonedTime } from "date-fns-tz";
import { useFormik } from "formik";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GroupBase, OptionsOrGroups } from "react-select";
import * as Yup from "yup";
import {
  useLazyOrganizationDriversQuery,
  useLazyOrganizationGeozonesQuery,
  useLazyOrganizationGroupsQuery,
  useLazyOrganizationVehiclesQuery,
  useOrganizationDriversQuery,
  useOrganizationGeozonesQuery,
  useOrganizationGroupsQuery,
  useOrganizationVehiclesQuery,
} from "../../../api/network/adminApiServices";
import { useGetReportSpecificFieldsQuery } from "../../../api/network/reportApiServices";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { TLegacyDriver } from "../../../api/types/Driver";
import { TLegacyGeozone } from "../../../api/types/Geozone";
import { OrgGHGroup, TLegacyGroup } from "../../../api/types/Group";
import { TReport, TReportFilter } from "../../../api/types/Report";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldCheckbox,
  AdminFormFieldCheckboxWithOnOffLabels,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldSubmit,
  PillItem,
  PseudoSelect,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HierarchyGroupSelector from "../../../components/common/hierarchyGroupSelector";
import {
  APP_CONFIG,
  REPORT_FILTER_BY_OPTION_VALUES,
  REPORT_TYPES,
  TIMEZONE_OPTIONS,
} from "../../../constants/constants";
import { useACL } from "../../../hooks/acl";
import { useMapInit } from "../../../hooks/mapInit";
import {
  datepickerValueToUTC,
  useAccountTimezone,
} from "../../../hooks/timezone";
import { asyncDebounce } from "../../../utils/common";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../../utils/dateTime";
import { driverDisplayName } from "../../../utils/driver";
import { geozoneDisplayName } from "../../../utils/geozone";
import { groupDisplayName } from "../../../utils/group";
import { useLoggedInUserData } from "../../../utils/user";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";
import GAutocompleteField from "../../map-fleet/gAutocompleteField";
import { REPORT_FILTER_TYPES } from "../constants";
import { filterIsMultiSelect } from "../reportUtil";
import {
  REPORT_ENTITIES,
  TMapRef,
  TReportTypes,
  TReportTypesWithUuidSubReportSlug,
} from "../type";
import { getReportEntity } from "../util";

export type TReportOptionsSubmitCallbackProps = {
  filterValues: any;
  reportType: string;
  category: number;
  reportId: number;
  navigate: any;
  reportFilters?: TReportFilter[];
  finallyCallback?: (requestSuccessful?: boolean) => void;
};
export type TReportOptionsSubmitCallback = (
  props: TReportOptionsSubmitCallbackProps
) => void;
export type TReportOptionsProps = {
  reportType: string; // keyof typeof REPORT_TYPES;
  reportId: number;
  subReportType?: TReportTypesWithUuidSubReportSlug; // TODO: deprecated
  entityId?: string; // TODO: we are now using Report Filters
  reportFilters?: any;
  customSubmitLabel?: string;
  onSubmit?: TReportOptionsSubmitCallback;
  onCancel?: () => void;
  extraWrapperClass?: string;
  showSubHeading?: boolean;
  scrollingWithinModal?: boolean;
};

const TIME_TOGGLE = {
  RANGE: false,
  CALENDAR: true,
};

const prefill_device_last_event_timestamp = false;
const prefill_driver_last_event_timestamp = false;

const debugComponent = false;

const ReportOptions: FC<TReportOptionsProps> = ({
  reportType,
  reportId,
  subReportType,
  entityId,
  reportFilters,
  customSubmitLabel,
  onSubmit,
  onCancel,
  extraWrapperClass = `col-span-6`,
  showSubHeading = false,
  scrollingWithinModal = false,
}) => {
  const { t: tMain } = useTranslation();
  const { t: tReports } = useTranslation("translation", {
    keyPrefix: "reports",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.optionsForm",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { onGAutocompletePlaceChanged } = useMapInit();

  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  // common map ref to be used for various map operations
  const mapRef = React.useRef<TMapRef>({
    map: null,
    objects: {},
  });

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  // retrieve account timezone
  const { convertToTZForDateFns, tzForDisplay } = useAccountTimezone();

  const reportsByType: { [key: string]: TReport[] } = useSelector(
    (state: any) => state.commonReducer.reportsByType
  );

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");
  const { getUserACLAccessLevel } = useACL();
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(
    reportFilters?.timezone !== tzForDisplay
  );

  // moved `useOrganizationVehiclesQuery` here to allow access to formik `values` object
  // fetching devices list
  const [initialDeviceListParams, setInitialDeviceListParams] = useState({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
    search: "",
    isactive:
      typeof reportFilters?.device_id_status_filter !== "undefined" // Use reportFilters here instead of values (which isn't defined yet)
        ? !!reportFilters?.device_id_status_filter
          ? 1
          : undefined
        : undefined,
  });
  const {
    data: dataInitialDevicesList,
    isFetching: isFetchingInitialDevicesList,
    refetch: refetchInitialDevicesList,
  } = useOrganizationVehiclesQuery(initialDeviceListParams);

  const getAssetLabelObject = (id: string) => {
    const asset = dataInitialDevicesList?.results?.find(
      (v: any) => v.deviceid.toString() === id.toString()
    );
    // If found in list, format it. If not, return ID.
    const label = asset
      ? consistentDisplayAssetItem(
          asset.deviceid,
          asset.description,
          asset.displayname,
          asset.drivername,
          showDriver
        )
      : id;

    return { value: id, label: label };
  };
  const { data: dataSpecificReport, isFetching: isFetchingSpecificReport } =
    useGetReportSpecificFieldsQuery({
      type: REPORT_TYPES[reportType as keyof typeof REPORT_TYPES],
      report_id: reportId,
      // category:
      //   reportsByType[reportType as keyof typeof REPORT_TYPES]
      //   ?.find((item: TReport) => item.id === reportId)
      //   ?.category ?? 0,
      // category: REPORT_CATEGORIES.ASSET_DETAIL,
      // REPORT_CATEGORIES[ //`${
      //   // reportsByType[reportType as keyof typeof REPORT_TYPES]
      //   // ?.find((item: TReport) => item.id === reportId)
      //   // ?.category || "" as keyof typeof REPORT_CATEGORIES,
      // // }`,
      // ],
      // page: 1,
      // page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
      // reportType as keyof typeof REPORT_TYPES,
    });
  // re-validate form on data fetch
  useEffect(() => {
    if (!!dataSpecificReport && !isFetchingSpecificReport) {
      formik?.validateForm();
    }
  }, [dataSpecificReport, isFetchingSpecificReport]);

  // fetching devices list
  // the `useOrganizationVehiclesQuery` API call for initial values is moved below
  // to allow access to formik `values` object

  const [orgVehiclesQuery, { isFetching: isFetchingOrgVehicles }] =
    useLazyOrganizationVehiclesQuery();
  const [vehileDropdownSearchResults, setVehicleDropdownSearchResults] =
    useState<TLegacyVehicle[]>([]);

  const loadOptionsHandlerVehicle = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: vehiclesQueryResponse } = await orgVehiclesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page || 2,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
      isactive:
        typeof values?.device_id_status_filter !== "undefined"
          ? !!values?.device_id_status_filter
            ? 1
            : undefined
          : undefined,
    });

    if (!vehiclesQueryResponse || !vehiclesQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: additional?.page || 2,
        },
      };
    }
    const { results } = vehiclesQueryResponse as {
      results: TLegacyVehicle[];
    };
    setVehicleDropdownSearchResults(results);
    return {
      options: !!results
        ? (results || ([] as TLegacyVehicle[])).map(
            (item: TLegacyVehicle, index: number) =>
              ({
                value: `${item.deviceid}`,
                label: consistentDisplayAssetItem(
                  item.deviceid,
                  item.description,
                  item.displayname,
                  item.drivername,
                  showDriver
                ),
                // label: `${item.deviceid}${item.description ? ` | ${item.description}` : ""}${item.displayname ? ` | ${item.displayname}` : ""}`,
              }) as TSelectboxOption
          )
        : [],
      hasMore: vehiclesQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerVehicle = asyncDebounce(
    loadOptionsHandlerVehicle,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  // fetching drivers list
  const {
    data: dataInitialDriversList,
    isFetching: isFetchingInitialDriversList,
  } = useOrganizationDriversQuery({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
    search: "",
  });

  const [orgDriversQuery, { isFetching: isFetchingOrgDrivers }] =
    useLazyOrganizationDriversQuery();
  const [driverDropdownSearchResults, setDriverDropdownSearchResults] =
    useState<TLegacyDriver[]>([]);

  const loadOptionsHandlerDriver = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: driversQueryResponse } = await orgDriversQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page || 2,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.DRIVER_DROPDOWN.join(","), // search by driverid only
    });

    if (!driversQueryResponse || !driversQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: additional?.page ?? 2,
        },
      };
    }
    const { results } = driversQueryResponse as {
      results: TLegacyDriver[];
    };
    setDriverDropdownSearchResults(results);
    return {
      options: !!results
        ? (results || ([] as TLegacyDriver[])).map(
            (item: TLegacyDriver, index: number) =>
              ({
                value: `${item.driverid}`,
                label: driverDisplayName(item),
              }) as TSelectboxOption
          )
        : [],
      hasMore: driversQueryResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerDriver = asyncDebounce(
    loadOptionsHandlerDriver,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  // fetching geozone list
  const {
    data: dataInitialGeozoneList,
    isFetching: isFetchingInitialGeozoneList,
  } = useOrganizationGeozonesQuery({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
    search: "",
  });

  const [orgGeozonesQuery, { isFetching: isFetchingOrgGeozones }] =
    useLazyOrganizationGeozonesQuery();
  // const [geozoneDropdownSearchResults, setGeozoneDropdownSearchResults] = useState<TLegacyGeozone[]>([]);

  const loadOptionsHandlerGeozone = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: geozonesQueryResponse } = await orgGeozonesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 2,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.GEOZONE_DROPDOWN.join(","),
    });

    if (!geozonesQueryResponse || !geozonesQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: additional?.page || 2,
        },
      };
    }
    const { results } = geozonesQueryResponse as {
      results: TLegacyGeozone[];
    };
    // setGeozoneDropdownSearchResults(results);
    return {
      options: !!results
        ? (results || ([] as TLegacyGeozone[])).map(
            (item: TLegacyGeozone, index: number) =>
              ({
                value: `${item.geozoneid}`,
                label: geozoneDisplayName(item),
              }) as TSelectboxOption
          )
        : [],
      hasMore: geozonesQueryResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerGeozone = asyncDebounce(
    loadOptionsHandlerGeozone,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  // fetching group list
  const { data: dataInitialGroupList, isFetching: isFetchingInitialGroupList } =
    useOrganizationGroupsQuery({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    });

  const [orgGroupsQuery, { isFetching: isFetchingOrgGroups }] =
    useLazyOrganizationGroupsQuery();
  // const [groupDropdownSearchResults, setGroupDropdownSearchResults] = useState<TLegacyGroup[]>([]);

  const loadOptionsHandlerGroup = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: groupsQueryResponse } = await orgGroupsQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 2,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.GROUP_DROPDOWN.join(","),
    });

    if (!groupsQueryResponse || !groupsQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: additional?.page ?? 2,
        },
      };
    }
    const { results } = groupsQueryResponse as { results: TLegacyGroup[] };
    // setGroupDropdownSearchResults(results);
    return {
      options: !!results
        ? (results || ([] as TLegacyGroup[])).map(
            (item: TLegacyGroup, index: number) =>
              ({
                value: `${item.groupid}`,
                label: groupDisplayName(item),
              }) as TSelectboxOption
          )
        : [],
      hasMore: groupsQueryResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerGroup = asyncDebounce(
    loadOptionsHandlerGroup,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const [basicReportFilters, setBasicReportFilters] = useState<TReportFilter[]>(
    []
  );
  useEffect(() => {
    if (!!dataSpecificReport) {
      setBasicReportFilters(
        dataSpecificReport?.report_filters?.filter(
          (filter) => filter.filter_type === REPORT_FILTER_TYPES.BASIC
        ) || []
      );
    }
  }, [dataSpecificReport]);

  const field_filter_by_present =
    !!basicReportFilters?.find(
      (item) =>
        item.param ===
        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name
    ) &&
    !!basicReportFilters?.find(
      (item) =>
        item.param ===
        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name
    );

  const deviceIdIsMutliSelect =
    !!dataSpecificReport?.report_filters &&
    !!filterIsMultiSelect(
      APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name,
      dataSpecificReport?.report_filters?.filter(
        (filter) => filter.filter_type === REPORT_FILTER_TYPES.BASIC
      ) || []
    );

  const deviceGroupIsMultiSelect =
    !!dataSpecificReport?.report_filters &&
    !!filterIsMultiSelect(
      APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name,
      dataSpecificReport?.report_filters?.filter(
        (filter) => filter.filter_type === REPORT_FILTER_TYPES.BASIC
      ) || []
    );

  // formik
  const memoizedInitialValues = useMemo(
    () => ({
      // default values
      ...basicReportFilters?.reduce((acc, item) => {
        acc[item.param as string] = item.value;
        if (item.param === "start_time") {
          acc[`start_time`] = item.value || "";
        }
        if (item.param === "time_range") {
          acc[`time_range`] =
            item.value || APP_CONFIG.REPORTS.TIME_RANGE_OPTIONS[0].value;
        }
        if (item.param === "timezone") {
          if (APP_CONFIG.DEBUG.ALL && !tzForDisplay)
            console.error("account timezone not found", tzForDisplay);
          else console.log("tzForDisplay", tzForDisplay);
          acc[`timezone`] = tzForDisplay;
        }
        if (
          item.param ===
          APP_CONFIG.REPORTS.GEOZONE_LOOKUP_LATITUDE_LONGITUDE[0].field_name
        ) {
          acc["address"] = "";
        }
        return acc;
      }, {} as any),

      // extra field for select ALL option
      ...basicReportFilters?.reduce((acc, item) => {
        const is_multi = !!Object.values(
          APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
        ).find((value, _) => value.field_name === item.param)?.multiple;
        const faux_option_all = !!Object.values(
          APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
        ).find((value, _) => value.field_name === item.param)?.faux_option_all;
        const active_status_toggle = !!Object.values(
          APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
        ).find((value, _) => value.field_name === item.param)
          ?.active_status_toggle;
        const allActiveDevicesSelected =
          item.value === APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE &&
          item.param ===
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name;
        const allActiveGroupsSelected =
          item.value === APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE &&
          item.param ===
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name;

        if (!!faux_option_all) {
          const actualFieldValue = !!is_multi
            ? basicReportFilters
                ?.find((field) => field.param === item.param)
                ?.value.split(",")
            : basicReportFilters?.find((field) => field.param === item.param)
                ?.value;
          if (APP_CONFIG.DEBUG.ALL)
            console.log(
              `${item.param}_all`,
              actualFieldValue,
              Array.isArray(actualFieldValue) && actualFieldValue.length > 0
                ? false
                : true
            );
          acc[`${item.param}_all`] =
            Array.isArray(actualFieldValue) && actualFieldValue.length > 0
              ? false
              : true;
        }
        if (
          !!active_status_toggle &&
          // with device_groups_isactive filter added directly to report definition (received from BE APIs),
          // we only need to handle device_id filter flags below
          item.param ===
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name
        ) {
          acc[`${item.param}_status_filter`] = true;
          acc[`${item.param}_all_filter`] =
            item.param ===
              APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name &&
            [
              APP_CONFIG.REPORTS.GROUP_ASSET_ALL_VALUE,
              APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE,
            ].includes(
              basicReportFilters?.find(
                (item) =>
                  item.param ===
                  APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name
              )?.value || ""
            )
              ? basicReportFilters?.find(
                  (item) =>
                    item.param ===
                    APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name
                )?.value === APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE
              : // if not initially set, default to true
                true;
          // allActiveDevicesSelected || allActiveGroupsSelected;
        }
        return acc;
      }, {} as any),

      // time toggle
      ...(!!basicReportFilters?.find((item) => item.param === "start_time") &&
      !!basicReportFilters?.find((item) => item.param === "time_range")
        ? {
            time_toggle: TIME_TOGGLE.RANGE,
          }
        : {}),

      // filter by
      ...(field_filter_by_present
        ? {
            filter_by: REPORT_FILTER_BY_OPTION_VALUES.GROUP,
          }
        : {}),

      // report filters passed from inner report listing page
      ...(!!reportFilters ? reportFilters : {}),

      // add time range if not present in report filters
      ...(!reportFilters?.time_range
        ? { time_toggle: TIME_TOGGLE.CALENDAR }
        : {}),

      // this block to run only for non-standard reports
      // ensure value for device_id is csv or array based on deviceIdIsMutliSelect
      ...basicReportFilters?.reduce((acc, item) => {
        if (
          reportType !== REPORT_TYPES.STANDARD &&
          item.param ===
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name
        ) {
          const deviceIdIsMulti = !!filterIsMultiSelect(
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name,
            dataSpecificReport?.report_filters?.filter(
              (filter) => filter.filter_type === REPORT_FILTER_TYPES.BASIC
            ) || []
          );
          console.log("BASIC INTERNAL deviceIdIsMulti", deviceIdIsMulti);
          console.log(
            "item.value type:",
            typeof item.value,
            "len:",
            item.value?.length,
            "val:",
            item.value
          );
          acc[item.param as string] = acc[item.param as string] =
            deviceIdIsMulti && item.value !== ""
              ? item.value.split(",").map((value) => getAssetLabelObject(value))
              : item.value;
        }
        return acc;
      }, {} as any),
    }),
    [
      dataSpecificReport,
      basicReportFilters,
      tzForDisplay,
      reportFilters,
      dataInitialDevicesList,
    ]
  );

  const formik = useFormik({
    initialValues:
      // memoizedInitialValues,
      {
        ...Object.keys(memoizedInitialValues).reduce((acc, key) => {
          const isMultiSelectField = Object.values(
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
          ).find((value) => value.field_name === key)?.multiple;

          // filter device_id / device_group based on multi select
          if (
            // key === APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name
            isMultiSelectField
          ) {
            // if device_id / device_group is multi select, and value is not array, convert it to array
            if (
              (deviceIdIsMutliSelect || deviceGroupIsMultiSelect) &&
              !Array.isArray(memoizedInitialValues[key]) &&
              memoizedInitialValues[key]?.length > 0
            ) {
              acc[key] = memoizedInitialValues[key]
                ?.split(",")
                .map((value: any) => ({
                  label: value,
                  value: value,
                }));
              // if device_id / device_group is not multi select, and value is array, convert it to csv
            } else if (
              !deviceIdIsMutliSelect &&
              !deviceGroupIsMultiSelect &&
              Array.isArray(memoizedInitialValues[key]) &&
              memoizedInitialValues[key]?.length > 0
            ) {
              acc[key] = memoizedInitialValues[key]
                .map((value: any) => value.value)
                .join(",");
            } else {
              acc[key] = memoizedInitialValues[key];
            }
          } else {
            acc[key] = memoizedInitialValues[key];
          }
          return acc;
        }, {} as any),
      },
    enableReinitialize: true,

    validationSchema: Yup.object().shape(
      {
        ...basicReportFilters?.reduce((acc, item) => {
          const isMulti = filterIsMultiSelect(item.param, basicReportFilters);
          const faux_option_all = !!Object.values(
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
          ).find((value, _) => value.field_name === item.param)
            ?.faux_option_all;
          switch (item.param) {
            case APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name: // 'device_id':
              if (!!faux_option_all) {
                acc[`${item.param}_all`] = Yup.boolean();
                acc[item.param as string] = isMulti
                  ? // multiple assets can be selected
                    (Yup.array() as any).when(
                      ["filter_by", `${item.param}_all`],
                      {
                        is: (filter_by: string, all: boolean) =>
                          (filter_by === REPORT_FILTER_BY_OPTION_VALUES.ASSET ||
                            typeof filter_by === "undefined") &&
                          !all,
                        then: () =>
                          Yup.array()
                            .required("Select at least one asset")
                            .min(1, "Select at least one asset")
                            .of(
                              Yup.object().shape({
                                label: Yup.string().required(),
                                value: Yup.string().required(),
                              })
                            ),
                      }
                    )
                  : (Yup.string() as any).when(
                      ["filter_by", `${item.param}_all`],
                      {
                        is: (filter_by: string, all: boolean) =>
                          (filter_by === REPORT_FILTER_BY_OPTION_VALUES.ASSET ||
                            typeof filter_by === "undefined") &&
                          !all,
                        then: () =>
                          Yup.string().required("Please select an asset"),
                      }
                    );
              }
              break;

            case APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DRIVER.field_name: // 'driver_id':
              acc[item.param as string] = isMulti
                ? Yup.array()
                    .required("Select at least one driver")
                    .min(1, "Select at least one driver")
                    .of(
                      Yup.object().shape({
                        label: Yup.string().required(),
                        value: Yup.string().required(),
                      })
                    )
                : Yup.string().required("Please select a driver");
              break;
            case APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GEOZONE.field_name: // 'geozone_id':
              acc[item.param as string] = isMulti
                ? Yup.array()
                    .required("Select at least one geozone")
                    .min(1, "Select at least one geozone")
                    .of(
                      Yup.object().shape({
                        label: Yup.string().required(),
                        value: Yup.string().required(),
                      })
                    )
                : Yup.string().required("Please select a geozone");
              break;
            case APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name: // 'group_id':
              if (!!faux_option_all) {
                acc[`${item.param}_all`] = Yup.boolean();
                acc[item.param as string] = isMulti
                  ? (Yup.array() as any).when(
                      ["filter_by", `${item.param}_all`],
                      {
                        is: (filter_by: string, all: boolean) =>
                          (filter_by === REPORT_FILTER_BY_OPTION_VALUES.GROUP ||
                            filter_by === undefined) &&
                          !all,
                        then: () =>
                          Yup.array()
                            .required("Select at least one group")
                            .min(1, "Select at least one group")
                            .of(
                              Yup.object().shape({
                                label: Yup.string().required(),
                                value: Yup.string().required(),
                              })
                            ),
                      }
                    )
                  : (Yup.string() as any).when(
                      ["filter_by", `${item.param}_all`],
                      {
                        is: (filter_by: string, all: boolean) =>
                          filter_by === REPORT_FILTER_BY_OPTION_VALUES.GROUP &&
                          !all,
                        then: () =>
                          Yup.string().required("Please select a group"),
                      }
                    );
              }
              break;

            case "time_toggle":
              acc[item.param as string] = Yup.boolean();
              break;

            case "start_time":
              acc[item.param as string] = Yup.number().when(
                "time_toggle",
                ([time_toggle], schema) => {
                  return schema
                    .test({
                      test: (value) => {
                        return !!value || time_toggle === TIME_TOGGLE.RANGE;
                      },
                      message: "Please select the start date/time",
                    })
                    .test({
                      name: "start_time_not_older_than_13_months",
                      test: (value) => {
                        if (!value || time_toggle === TIME_TOGGLE.RANGE) return true;
                        const thirteenMonthsAgoDate = new Date();
                        thirteenMonthsAgoDate.setMonth(thirteenMonthsAgoDate.getMonth() - 13);
                        const thirteenMonthsAgoTimestamp = thirteenMonthsAgoDate.getTime() / 1000;
                        return Number(value) >= thirteenMonthsAgoTimestamp;
                      },
                      message:
                        "If data older than 13 months is needed please contact your account manager",
                    });
                }
              );
              break;

            case "end_time":
              acc[item.param as string] = Yup.number().when(
                ["start_time", "time_toggle"],
                ([start_time, time_toggle], schema) => {
                  return schema
                    .test({
                      test: (value) => {
                        return !!value || time_toggle === TIME_TOGGLE.RANGE;
                      },
                      message: "Please select the end date/time",
                    })
                    .test({
                      test: (value) => {
                        return (
                          (!!value && Number(value) > Number(start_time)) ||
                          time_toggle === TIME_TOGGLE.RANGE
                        );
                      },
                      message:
                        "Date To must be a date or time later than Date From",
                    })
                    .test({
                      name: "end_time_not_older_than_13_months",
                      test: (value) => {
                        if (!value || time_toggle === TIME_TOGGLE.RANGE) return true;
                        const thirteenMonthsAgoDate = new Date();
                        thirteenMonthsAgoDate.setMonth(thirteenMonthsAgoDate.getMonth() - 13);
                        const thirteenMonthsAgoTimestamp = thirteenMonthsAgoDate.getTime() / 1000;
                        return Number(value) >= thirteenMonthsAgoTimestamp;
                      },
                      message:
                        "If data older than 13 months is needed please contact your account manager",
                    });
                }
              );
              break;

            case "time_range":
              acc[item.param as string] = Yup.date().when(
                "time_toggle",
                ([time_toggle], schema) => {
                  return schema.test({
                    test: (value) => {
                      return !!value || time_toggle === TIME_TOGGLE.CALENDAR;
                    },
                    message: "Please select a time range",
                  });
                }
              );
              break;

            case "start_time":
            case "end_time":
            case "time_range":
            case "ds_time_range":
              // TODO: need to add validations
              // add https://www.npmjs.com/package/react-date-range library and
              // add validations for time range, date range fields
              break;

            // validation for time range, start time, end time
            // case 'time_range':
            //   acc[item.param as string] = Yup.string().when(['start_time', 'end_time'], {
            //     is: (startTime: string, endTime: string) => !startTime && !endTime,
            //     then: Yup.string().required("Please select the time range"),
            //   });
            //   break;
            // case 'start_time':
            //   acc[item.param as string] = Yup.string().when('time_range', {
            //     is: (timeRange: string) => !timeRange,
            //     then: Yup.string().required("Please select the start date/time"),
            //   });
            //   break;
            // case 'end_time':
            //   acc[item.param as string] = Yup.string().when('time_range', {
            //     is: (timeRange: string) => !timeRange,
            //     then: Yup.string().required("Please select the end date/time"),
            //   });
            //   break;

            // validation for time range, start time, end time
            // case 'time_range':
            //   acc[item.param as string] = Yup.string().when(['start_time', 'end_time'], ([start_time, end_time], schema) => {
            //     return schema.test({
            //       test: (value) => {
            //         return !!value || (!!start_time && !!end_time);
            //       },
            //       message: 'Please select the time range',
            //     });
            //   });
            //   break;
            // case 'start_time':
            //   acc[item.param as string] = Yup.string().when('time_range', ([time_range], schema) => {
            //     return schema.test({
            //       test: (value) => {
            //         return !!value || !!time_range;
            //       },
            //       message: 'Please select the start date/time',
            //     });
            //   });
            //   break;
            // case 'end_time':
            //   acc[item.param as string] = Yup.string().when('time_range', ([time_range], schema) => {
            //     return schema.test({
            //       test: (value) => {
            //         return !!value || !!time_range;
            //       },
            //       message: 'Please select the end date/time',
            //     });
            //   });
            //   break;

            case "timezone":
              acc[item.param as string] = Yup.string().required(
                "Please select the timezone"
              );
              break;

            case APP_CONFIG.REPORTS.GEOZONE_LOOKUP_LATITUDE_LONGITUDE[0]
              .field_name:
              acc["address"] = Yup.string().when(
                ["geozone_lookup_add_lat", "geozone_lookup_add_long"],
                ([geozone_lookup_add_lat, geozone_lookup_add_long], schema) => {
                  return schema.test({
                    test: (value) => {
                      return (
                        !!geozone_lookup_add_lat && !!geozone_lookup_add_long
                      );
                    },
                    message:
                      "Please enter an address and press the arrow button",
                  });
                }
              );
              break;

            default:
              const fieldIsOptional = !!Object.values(
                APP_CONFIG.REPORTS.ADDITIONAL_DROPDOWN_VALUES
              ).find((value, _) => value.field_name === item.param)?.optional;
              acc[item.param as string] = fieldIsOptional
                ? Yup.string()
                : Yup.string().required("This value is required"); // YupValidationSchemaVehicle(item);
          }
          return acc;
        }, {} as any),
        // address: Yup.string().required("Please enter the address"),
        // .test("address-found", "Please enter the address", function (value) {
        //   const { geozone_lookup_add_lat, geozone_lookup_add_long } =
        //     this.parent;
        //   if (!geozone_lookup_add_lat || !geozone_lookup_add_long) {
        //     return false;
        //   }
        //   return true;
        // }),
      } //, [['time_range', 'start_time']]
    ),

    onSubmit: (values, { setSubmitting }) => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setSeconds(0, 0);
      const today = new Date();
      today.setSeconds(0, 0);

      if (APP_CONFIG.DEBUG.REPORTS) console.log("submitting", values);

      const newValues = {
        ...values,

        // if device_id_all is true, set device_id to * (all) or @ (all active)
        ...(values[
          `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name}_all`
        ]
          ? {
              [APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name]:
                values[
                  `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name}_all_filter`
                ]
                  ? APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE
                  : APP_CONFIG.REPORTS.GROUP_ASSET_ALL_VALUE,
              // and set device_groups to empty
              [APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name]: [],
            }
          : {}),

        // if device_groups_all is true, set device_groups to * (all) or @ (all active)
        ...(values[
          `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name}_all`
        ]
          ? {
              [APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name]:
                values[APP_CONFIG.REPORTS.GROUP_ASSETS_FILTER.field_name] ===
                "1"
                  ? APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE
                  : APP_CONFIG.REPORTS.GROUP_ASSET_ALL_VALUE,
              // and set device_id to empty
              [APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name]: [],
            }
          : {}),

        // clear time_range if calendar is selected
        ...(values.time_toggle === TIME_TOGGLE.CALENDAR
          ? { time_range: "" }
          : {}),
      };

      if (APP_CONFIG.DEBUG.REPORTS) console.log("newValues", newValues);

      onSubmit &&
        onSubmit({
          filterValues: newValues,
          reportType,
          category: dataSpecificReport?.category || 0,
          reportId,
          navigate,
          reportFilters: basicReportFilters,
          finallyCallback: () => {
            setSubmitting(false);
          },
        });
      dispatch(
        setModalsData({
          ...modalsState,
          showReportTypeSelector: false,
          showUpdateReportOptions: false,
          showReportOptions: false
        })
      );
      if (APP_CONFIG.DEBUG.REPORTS)
        console.log("formik onSubmit values", newValues);
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
    setValues,
    setFieldTouched,
    setFieldError,
  } = formik;

  // the two useEffects below to keep the device_id_status_filter and device_id_status_filter in sync, as they are used in different places but represent the same filter
  // this results in the 'Search from ____ assets' label to update based on the status filter, and also ensures correct filtering of vehicles in dropdown based on status filter
  useEffect(() => {
    if (
      typeof values?.device_id_status_filter !== "undefined" &&
      !!values?.device_id_status_filter !==
        !!values?.[
          `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name}_status_filter`
        ]
    ) {
      setFieldValue(
        `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name}_status_filter`,
        !!values?.device_id_status_filter
      );
    }
  }, [values]);
  useEffect(() => {
    // update params to use values from formik for device_id_status_filter, to ensure correct filtering of vehicles in dropdown
    if (typeof values?.device_id_status_filter !== "undefined") {
      setInitialDeviceListParams({
        accountid: `${thisUserOrganizationId}`,
        page: 1,
        page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
        search: "",
        isactive:
          typeof values?.device_id_status_filter !== "undefined"
            ? !!values?.device_id_status_filter
              ? 1
              : undefined
            : undefined,
      });
    }
  }, [values?.device_id_status_filter]);

  if (APP_CONFIG.DEBUG.REPORTS && Object.keys(errors).length > 0)
    console.log("errors", errors);
  // if (APP_CONFIG.DEBUG.REPORTS) console.log('values', values);
  useEffect(() => {
    console.log("TZ CHANGED", values.timezone);
  }, [values.timezone]);

  // calculate unix timestamp from date and time
  useEffect(() => {
    // if(!touched.start_time
    // || !touched.end_time) return;
    // if(!touched?.device_id && !touched?.driver_id) return;
    // calculateUpdateUnixTimestamps(values);
    if (APP_CONFIG.DEBUG.REPORTS) console.log("values change triggered");
    const newValues = values; // getValuesWithCalcUnixTimestamps(values);
    setValues(newValues);
    setShowTimezoneDropdown(newValues.timezone !== tzForDisplay);
  }, [
    values.timezone,
    values.start_time,
    values.end_time,
    touched.timezone,
    touched.start_time,
    touched.end_time,
  ]);

  const handleChangeFilterBy = (e: TSelectboxOption | null) => {
    if (!e) return;
    setFieldValue("filter_by", e.value);

    // reset group / asset selection on change of filter_by
    if (e.value === REPORT_FILTER_BY_OPTION_VALUES.GROUP) {
      setFieldValue(
        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name,
        ""
      );
      if (
        typeof values[
          `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name}_all`
        ] !== "undefined"
      ) {
        setFieldValue(
          `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name}_all`,
          false
        );
      }
    } else {
      setFieldValue(
        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name,
        ""
      );
      if (
        typeof values[
          `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name}_all`
        ] !== "undefined"
      ) {
        setFieldValue(
          `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name}_all`,
          false
        );
      }
    }

    // ensures display of updated applicable errors after filter_by change
    setTimeout(() => {
      formik.validateForm();
    }, 200);
  };

  const handleChangeDeviceId = (newValue: any) => {
    console.log("newValue#", newValue);
    const deviceId = Array.isArray(newValue)
      ? newValue.map((value: any) => value.value).join(",")
      : "";
    const deviceIdArray = deviceId.split(",");
    const selectedVehicle =
      dataInitialDevicesList?.results?.find((item: TLegacyVehicle) =>
        deviceIdArray.includes(item.deviceid)
      ) ??
      vehileDropdownSearchResults.find((item) =>
        deviceIdArray.includes(item.deviceid)
      );
    const selectedVehicleTS = selectedVehicle?.lasteventtimestamp;
    const startDateObj = !!selectedVehicleTS
      ? new Date(dbTimeToJsTime(selectedVehicleTS - 3600 * 24 * 1))
      : false;
    if (!!startDateObj) {
      startDateObj.setSeconds(0, 0);
    }
    const endDateObj = !!selectedVehicleTS
      ? new Date(dbTimeToJsTime(selectedVehicleTS))
      : false;
    if (!!endDateObj) {
      endDateObj.setSeconds(0, 0);
    }
    const newValues = {
      ...values,
      device_id: deviceId, // Store as a comma-separated string
      ...(prefill_device_last_event_timestamp
        ? {
            start_time:
              !!selectedVehicleTS && !!startDateObj
                ? startDateObj.toISOString().replace(":00.000Z", "")
                : "",
            end_time:
              !!selectedVehicleTS && !!endDateObj
                ? endDateObj.toISOString().replace(":00.000Z", "")
                : "",
          }
        : {}),
    };
    // setValues(newValues);
    // calculateUpdateUnixTimestamps(newValues);
    // const newValuesWithCalculatedUnixTimestamps =
    //   getValuesWithCalcUnixTimestamps(newValues);
    console.log("newValues@#", newValues);
    setValues(newValues);
    setTimeout(() => {
      formik?.validateForm();
    }, 100); // intentional delay to reset validation error when option is selected
  };

  const handleChangeDriverId = (newValue: string) => {
    const selectedDriver =
      dataInitialDriversList?.results?.find(
        (item: TLegacyDriver) => item.driverid === newValue
      ) ??
      driverDropdownSearchResults.find((item) => item.driverid === newValue);
    const selectedDriverTS = selectedDriver?.lastupdatetime;
    const startDateObj = !!selectedDriverTS
      ? new Date(dbTimeToJsTime(selectedDriverTS - 3600 * 24 * 1))
      : false;
    if (!!startDateObj) {
      startDateObj.setSeconds(0, 0);
    }
    const endDateObj = !!selectedDriverTS
      ? new Date(dbTimeToJsTime(selectedDriverTS))
      : false;
    if (!!endDateObj) {
      endDateObj.setSeconds(0, 0);
    }
    const newValues = {
      ...values,
      driver_id: newValue,
      ...(prefill_driver_last_event_timestamp
        ? {
            start_time:
              !!selectedDriverTS && !!startDateObj
                ? startDateObj.toISOString().replace(":00.000Z", "")
                : "",
            end_time:
              !!selectedDriverTS && !!endDateObj
                ? endDateObj.toISOString().replace(":00.000Z", "")
                : "",
          }
        : {}),
    };
    // setValues(newValues);
    // calculateUpdateUnixTimestamps(newValues);
    // const newValuesWithCalculatedUnixTimestamps =
    //   getValuesWithCalcUnixTimestamps(newValues);
    setValues(newValues);
    // setFieldValue("driver_name", selectedDriver?.displayname ?? selectedDriver?.driverid);
    // setFieldValue("driver_phone_number", selectedDriver?.contactphone);
    setTimeout(() => {
      formik?.validateForm();
    }, 100); // intentional delay to reset validation error when option is selected
  };

  // set initial values on entityId change, and on component mount
  useEffect(() => {
    if (!!entityId) {
      if (
        getReportEntity(reportType as TReportTypes) === REPORT_ENTITIES.DRIVER
      ) {
        handleChangeDriverId(entityId);
      } else {
        handleChangeDeviceId(entityId);
      }
    }
  }, [entityId]);

  const invalidFields = Object.keys(errors)
    // .filter(key => !!errors[key as keyof typeof errors]);
    .filter((key) => !!errors[key]);
  const isFormValid =
    invalidFields.length === 0 &&
    (!!reportFilters
      ? true // if report filters are passed (inner report listing page), then form is valid even if not dirty
      : dirty || true); // 02/03/2025: always return true if not dirty - there could be cases where reportFilters are not present/needed

  let dropdown_fields: Map<string, TReportFilter> = new Map();
  let prioritized_dropdown_fields: Map<string, TReportFilter> = new Map();
  Object.keys(APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS)
    .map(
      (filter) =>
        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
          filter as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
        ].field_name
    ) // device_id, driver_id, geozone_id, group_id
    .forEach((fieldParam) => {
      const field_object = basicReportFilters?.find(
        (item) => item.param === fieldParam
      );
      if (!!field_object) {
        if (
          fieldParam ===
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name ||
          fieldParam ===
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name
        ) {
          prioritized_dropdown_fields.set(fieldParam, field_object);
        } else {
          dropdown_fields.set(fieldParam, field_object);
        }
        if (!!field_object?.value) {
          // setFieldValue(fieldParam, field_object.value);
        }
      }
    });
  // re-sort dropdown_fields to prioritize device_id and device_groups over other fields
  dropdown_fields = new Map([
    ...Array.from(prioritized_dropdown_fields.entries()),
    ...Array.from(dropdown_fields.entries()),
  ]);

  const field_group = basicReportFilters?.find(
    (item) =>
      item.param === APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name
  );
  const field_asset = basicReportFilters?.find(
    (item) =>
      item.param === APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name
  );

  const field_time_range = basicReportFilters?.find(
    (item) => item.param === "time_range"
  );
  // if (!!field_time_range?.value) {
  //   setFieldValue("time_range", field_time_range.value);
  // }

  const field_start_time = basicReportFilters?.find(
    (item) =>
      /*item.field.includes('timestamp') &&*/ item.param === "start_time"
  );
  // if (!!field_start_time?.value) {
  //   setFieldValue("start_time", field_start_time.value);
  // }

  const field_end_time = basicReportFilters?.find(
    (item) => /*item.field.includes('timestamp') &&*/ item.param === "end_time"
  );
  // if (!!field_end_time?.value) {
  //   setFieldValue("end_time", field_end_time.value);
  // }

  const handleDatetimePickerChange = (fieldName: string, value: any) => {
    if (APP_CONFIG.DEBUG.REPORTS) console.log("==", fieldName, value);
    const selectedDateString = value; // YYYY-MM-DDTHH:mm
    const selectedDate = new Date(value);
    const utcDate = fromZonedTime(
      selectedDate,
      convertToTZForDateFns(values.timezone)
    );
    const jsTime = new Date(utcDate).getTime();
    if (APP_CONFIG.DEBUG.REPORTS) console.log("timestamp", jsTime / 1000);
    setFieldValue(fieldName, jsTime / 1000);
    if (fieldName === "start_time") {
      // prefill end time with start time + (1 day - 1 second, i.e. 23:59:59)
      const endDate = new Date(jsTime);
      endDate.setDate(endDate.getDate());
      setFieldValue(
        "end_time",
        endDate
          ? datepickerValueToUTC(
              `${endDate.toISOString().split("T")[0]}T23:59:59`,
              values.timezone
            )
          : ""
      );
    }
  };

  // trigger formik validation on mount
  useEffect(() => {
    setTimeout(() => {
      formik.validateForm();
    }, 200);
  }, []);

  const handleUpdateGeozoneOnNewlySelectedCoordinates = (
    lat: number,
    lng: number
  ) => {
    console.log("handleUpdateGeozoneOnNewlySelectedCoordinates", lat, lng);
    // update lat_lng field value
    setFieldValue("geozone_lookup_add_lat", lat);
    setFieldValue("geozone_lookup_add_long", lng);
  };

  const onGAutocompletePlaceChangeFinished: (
    place: google.maps.LatLng | undefined
  ) => void = (place) => {
    if (!!place?.lat() && !!place?.lng()) {
      handleUpdateGeozoneOnNewlySelectedCoordinates(place?.lat(), place?.lng());
    }
  };

  if (isFetchingSpecificReport) {
    return (
      <div className="flex justify-between items-center w-full h-72">
        <div
          className={`absolute inset-0 flex items-center justify-center bg-opacity-90 z-50`}
        >
          <img
            src={svgIcons.LoadingAnimation}
            alt="Loading animation"
            className="w-12 h-12"
          />
        </div>
      </div>
    );
  }

  const getGeozoneOption = (geozoneId: string): TSelectboxOption => {
    const geozone = dataInitialGeozoneList?.results?.find(
      (item) => `${item.geozoneid}` === geozoneId
    );

    return {
      value: geozoneId,
      label: geozone
        ? geozoneDisplayName(geozone)
        : geozoneDisplayName({ geozoneid: geozoneId }),
    };
  };

  return (
    <>
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && debugComponent && (
        <div className="Xabsolute top-2 left-2 flex gap-4 text-[8px] z-10 bg-white/80">
          <pre>
            [definition]
            <br />
            basicReportFilters (truncated):
            <br />
            {JSON.stringify(
              basicReportFilters.reduce?.((acc, item) => {
                acc[item.param] = item.value;
                return acc;
              }, {} as any) || {},
              null,
              2
            )}
          </pre>
          <pre>
            deviceIdIsMutliSelect: {deviceIdIsMutliSelect?.toString()}
            <br />
            deviceGroupIsMultiSelect: {deviceGroupIsMultiSelect?.toString()}
            <br />
            memoizedInitialValues:
            <br />
            {JSON.stringify(memoizedInitialValues, null, 2)}
          </pre>
          <pre>
            reportFilters:
            <br />
            {JSON.stringify(reportFilters, null, 2)}
          </pre>
        </div>
      )}
      {/* Sub Heading - included as this should show the text conditionally based on available filters */}
      {showSubHeading && !!dataSpecificReport && (
        <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
          {t(
            basicReportFilters?.length > 0
              ? "sub_heading"
              : "no_filters_continue_message"
          )}
        </p>
      )}
      {/* Form */}
      <form
        className={`${extraWrapperClass} flex flex-col gap-4`}
        onSubmit={handleSubmit}
      >
        <div
          className={
            scrollingWithinModal
              ? userIsImpersonated
                ? `max-h-[calc(100vh-370px-24px)] overflow-auto` // + 40px for impersonation (blue) bar and + 24px for padding
                : `max-h-[calc(100vh-330px)] overflow-auto`
              : ""
          }
        >
          <div className={`grid grid-cols-6 gap-6 ${extraWrapperClass}`}>
            <div className="fields-wrapper col-span-6 grid grid-cols-6 gap-6">
              {/* max-h-[calc(100vh-350px)] overflow-auto px-1 */}

              {/* [Filter by] Toggle between Group and Asset */}
              {typeof values?.filter_by !== "undefined" &&
                !!field_group &&
                !!field_asset && (
                  <AdminFormFieldDropdown
                    label={t("filter_by")}
                    name="filter_by"
                    id="filter_by"
                    options={APP_CONFIG.REPORTS.FILTER_BY_GROUP_ASSET_OPTIONS.map(
                      (item) => ({
                        value: item.value,
                        label: t(`filter_by_${item.value}`),
                      })
                    )}
                    onChange={handleChangeFilterBy}
                    onBlur={handleBlur}
                    value={values.filter_by}
                    touched={touched.filter_by as boolean}
                    error={errors.filter_by as string}
                    customWrapperClass="col-span-6"
                  />
                )}

              {/* Dropdown Fields like Device ID, Driver ID, Zone ID */}
              {Array.from(dropdown_fields.keys()).map((fieldParam, i) => {
                const fieldObject = dropdown_fields.get(
                  fieldParam
                ) as TReportFilter;
                const isMulti = filterIsMultiSelect(
                  fieldParam,
                  basicReportFilters
                );
                const faux_option_all = !!Object.values(
                  APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                ).find((value, _) => value.field_name === fieldParam)
                  ?.faux_option_all;
                const active_status_toggle = !!Object.values(
                  APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                ).find((value, _) => value.field_name === fieldParam)
                  ?.active_status_toggle;
                const fieldValue = values[fieldObject.param];
                // if filter_by is applicable (i.e. both group and asset are present), then hide the dropdowns based on the selected filter_by value
                if (
                  typeof values?.filter_by !== "undefined" &&
                  values.filter_by === REPORT_FILTER_BY_OPTION_VALUES.GROUP &&
                  fieldParam ===
                    APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name
                )
                  return null;
                if (
                  typeof values?.filter_by !== "undefined" &&
                  values.filter_by === REPORT_FILTER_BY_OPTION_VALUES.ASSET &&
                  fieldParam ===
                    APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name
                )
                  return null;

                return (
                  <React.Fragment key={i}>
                    {(fieldObject.param ===
                      APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE
                        .field_name &&
                      isFetchingInitialDevicesList) ||
                    (fieldObject.param ===
                      APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DRIVER
                        .field_name &&
                      isFetchingInitialDriversList) ||
                    (fieldObject.param ===
                      APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GEOZONE
                        .field_name &&
                      isFetchingInitialGeozoneList) ||
                    (fieldObject.param ===
                      APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP
                        .field_name &&
                      isFetchingInitialGroupList) ? (
                      <PseudoSelect label={t(fieldObject.param)} />
                    ) : (
                      <>
                        {/* in case of all assets/groups selected - add checkbox to select either all (active+inactive) or active-only */}
                        {faux_option_all &&
                          ((fieldObject.param ===
                            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE
                              .field_name &&
                            !!values[
                              `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE.field_name}_all`
                            ]) ||
                            (fieldObject.param ===
                              APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP
                                .field_name &&
                              !!values[
                                `${APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP.field_name}_all`
                              ])) && ( // getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ACTIVE_INACTIVE)>=ROLE_ACCESS_LEVELS.READ &&
                            <AdminFormFieldCheckboxWithOnOffLabels
                              customWrapperClass="col-span-6 -mb-2"
                              label={""}
                              offLabel={t(
                                `${fieldObject.param}_all_filter_off`
                              )}
                              offLabelDescription={t(
                                `${fieldObject.param}_all_filter_off_descr`
                              )}
                              onLabel={t(`${fieldObject.param}_all_filter_on`)}
                              onLabelDescription={t(
                                `${fieldObject.param}_all_filter_on_descr`
                              )}
                              id={`${fieldObject.param}_all_filter`}
                              type="checkbox"
                              name={`${fieldObject.param}_all_filter`}
                              checked={
                                fieldObject.param ===
                                APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP
                                  .field_name
                                  ? values[
                                      APP_CONFIG.REPORTS.GROUP_ASSETS_FILTER
                                        .field_name
                                    ] === "1"
                                  : values[`${fieldObject.param}_all_filter`]
                              }
                              onChange={(e) => {
                                if (
                                  fieldObject.param ===
                                  APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                                    .GROUP.field_name
                                ) {
                                  setFieldValue(
                                    APP_CONFIG.REPORTS.GROUP_ASSETS_FILTER
                                      .field_name,
                                    e.target.checked ? "1" : "0"
                                  );
                                } else {
                                  setFieldValue(
                                    `${fieldObject.param}_all_filter`,
                                    e.target.checked
                                  );
                                }
                              }}
                              onBlur={handleBlur}
                              touched={touched.time_toggle as boolean}
                              error={errors.time_toggle as string}
                            />
                          )}
                        {/* All Groups Checkbox */}
                        {faux_option_all && // if device_id is multi select, then show the all checkbox only if device_groups is also present as a filter
                          ((fieldParam ===
                            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE
                              .field_name &&
                            isMulti) ||
                            // for other fields, show the all checkbox
                            fieldParam !==
                              APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.DEVICE
                                .field_name) && (
                            <AdminFormFieldCheckbox
                              type="checkbox"
                              name={`${fieldObject.param}_all`}
                              id={`${fieldObject.param}_all`}
                              label={t(`${fieldObject.param}_all`)}
                              customWrapperClass="col-span-6"
                              checked={values[`${fieldObject.param}_all`]}
                              onChange={(e) => {
                                // reset the field value to empty array or string when this is checked, to trigger validation and make the user select items as applicable
                                formik.setFieldValue(
                                  fieldObject.param,
                                  isMulti ? [] : ""
                                ); // TODO: there's a validation bug here, unchecking all shows the error groups dropdown even if items are selected, or clearing items does not clear the pills on the multi select dropdown UI
                                // update the _all field value
                                setFieldValue(
                                  `${fieldObject.param}_all`,
                                  e.target.checked
                                );
                                // to trigger validation
                                setTimeout(() => {
                                  setFieldTouched(`${fieldObject.param}_all`);
                                }, 100);
                                // conditional checkbox updates
                                // disabled for good - prevent toggling every time all filter is toggled
                                // if (e.target.checked) {
                                //   // if checked, then default the _all_filter to true
                                //   setFieldValue(
                                //     `${fieldObject.param}_all_filter`,
                                //     true
                                //   );
                                // } else {
                                //   // if unchecked, then default the _status_filter to true
                                //   setFieldValue(
                                //     `${fieldObject.param}_status_filter`,
                                //     true
                                //   );
                                // }
                              }}
                            />
                          )}
                        {/* Active Status Toggle */}
                        {active_status_toggle &&
                          !values[`${fieldObject.param}_all`] && ( // getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ACTIVE_INACTIVE)>=ROLE_ACCESS_LEVELS.READ &&
                            <AdminFormFieldCheckboxWithOnOffLabels
                              customWrapperClass="col-span-6 -mb-8"
                              label={""}
                              offLabel={t(
                                `${fieldObject.param}_all_filter_off`
                              )}
                              offLabelDescription={t(
                                `${fieldObject.param}_all_filter_off_descr`
                              )}
                              onLabel={t(`${fieldObject.param}_all_filter_on`)}
                              onLabelDescription={t(
                                `${fieldObject.param}_all_filter_on_descr`
                              )}
                              id={`${fieldObject.param}_status_filter`}
                              type="checkbox"
                              name={`${fieldObject.param}_status_filter`}
                              checked={
                                fieldObject.param ===
                                APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP
                                  .field_name
                                  ? values[
                                      APP_CONFIG.REPORTS.GROUP_ASSETS_FILTER
                                        .field_name
                                    ] === "1"
                                  : values[`${fieldObject.param}_status_filter`]
                              }
                              onChange={(e) => {
                                if (
                                  fieldObject.param ===
                                  APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                                    .GROUP.field_name
                                ) {
                                  setFieldValue(
                                    APP_CONFIG.REPORTS.GROUP_ASSETS_FILTER
                                      .field_name,
                                    e.target.checked ? "1" : "0"
                                  );
                                } else {
                                  setFieldValue(
                                    `${fieldObject.param}_status_filter`,
                                    e.target.checked
                                  );
                                }
                              }}
                              onBlur={handleBlur}
                              touched={touched.time_toggle as boolean}
                              error={errors.time_toggle as string}
                            />
                          )}
                        {/* Device/Driver/Geozone/Group Selector */}
                        {(!faux_option_all ||
                          !values[`${fieldObject.param}_all`]) &&
                          (!faux_option_all ||
                            !values[`${fieldObject.param}_all`]) && (
                            <>
                              {/* Group (Hierarchial) Selector */}
                              {accountHasGroupHierarchyEnabled === "1" &&
                              fieldParam ===
                                APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GROUP
                                  .field_name ? (
                                <>
                                  {errors[fieldObject.param] && (
                                    <div className="col-span-6 bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs">
                                      {/* {typeof errors[fieldObject.param] === 'object' ? errors?.[fieldObject?.param]?.join(', ') : errors[fieldObject.param]} */}
                                      {/* {errors[fieldObject.param] as string} */}
                                      {
                                        'Either select "All Groups" option above, or at least one group from the option below'
                                      }
                                    </div>
                                  )}
                                  <AdminFormFieldSubmit
                                    label={tMain("select_groups_button")}
                                    type="button"
                                    variant="primary-like"
                                    customWrapperClass="col-span-6"
                                    onClick={() => {
                                      dispatch(
                                        setModalsData({
                                          ...modalsState,
                                          showHierarchyGroupSelector: true,
                                        })
                                      );
                                    }}
                                    disabled={!!values.all_groups} // disable if all groups is selected
                                  />
                                  {modalsState.showHierarchyGroupSelector && (
                                    <HierarchyGroupSelector
                                      renderAboveImpersonationBar
                                      initiallySelectedGroups={
                                        !!values[fieldObject.param] &&
                                        Array.isArray(values[fieldObject.param])
                                          ? values[fieldObject.param].map(
                                              (item: {
                                                label: string;
                                                value: string;
                                              }) => item.value
                                            )
                                          : []
                                      }
                                      onSelectionChange={(
                                        selectedGroups: string[]
                                      ) => {
                                        if (APP_CONFIG.DEBUG.DISPLAY_VERBOSE)
                                          console.log(
                                            "VALUES RECEIVED:",
                                            selectedGroups
                                          );
                                        setFieldValue(
                                          fieldParam,
                                          selectedGroups.map((item) => ({
                                            label: item,
                                            value: item,
                                          }))
                                        );
                                        setTimeout(() => {
                                          setFieldTouched(fieldParam);
                                        }, 100); // to trigger validation
                                      }}
                                    />
                                  )}
                                  <div className="col-span-6 grid grid-cols-12">
                                    <AdminFormFieldPillList
                                      disabled={values.all_groups}
                                      customWrapperClass="col-span-12"
                                    >
                                      {/* isError={errors[fieldObject.param] as boolean}> */}
                                      {!!values[fieldObject.param] &&
                                        values[fieldObject.param].map(
                                          (
                                            group: {
                                              label: string;
                                              value: string;
                                            },
                                            i: number
                                          ) => (
                                            <AdminFormFieldPillItem
                                              key={`pillItem_${i}`}
                                              disabled={values.all_groups}
                                              item={
                                                {
                                                  name: group.value,
                                                } as PillItem
                                              }
                                              onRemove={(val) => {
                                                const newGroups = values[
                                                  fieldObject.param
                                                ].filter(
                                                  (item: {
                                                    label: string;
                                                    value: string;
                                                  }) =>
                                                    item?.label !== val?.name
                                                );
                                                setFieldValue(
                                                  fieldParam,
                                                  newGroups
                                                );
                                                setTimeout(() => {
                                                  setFieldTouched(fieldParam);
                                                }, 100); // to trigger validation
                                              }}
                                              onItemClick={
                                                accountHasGroupHierarchyEnabled ===
                                                "1"
                                                  ? (item) => {
                                                      dispatch(
                                                        setModalsData({
                                                          ...modalsState,
                                                          showReadOnlyViewForHierarchyGroup:
                                                            {
                                                              accountid:
                                                                thisUserOrganizationId,
                                                              active_inactive_vehicle_counts:
                                                                {
                                                                  active_vehicle_count: 0,
                                                                  inactive_vehicle_count: 0,
                                                                },
                                                              description: "",
                                                              groupid:
                                                                group.value,
                                                            } as OrgGHGroup,
                                                          showHierarchyGroupSelector:
                                                            true,
                                                        })
                                                      );
                                                    }
                                                  : undefined
                                              }
                                            />
                                          )
                                        )}
                                    </AdminFormFieldPillList>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {/* Device / Driver / Geozone / Group (Non-Hierarchial) Selector */}
                                  <AdminFormFieldAsyncLazyDropdown
                                    key={`${fieldParam}_${(
                                      values[
                                        fieldObject.param ===
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.GROUP
                                          .field_name
                                          ? APP_CONFIG.REPORTS
                                              .GROUP_ASSETS_FILTER.field_name
                                          : `${fieldObject.param}_status_filter`
                                      ] || ""
                                    )?.toString()}`} // to force re-render on status filter change
                                    customWrapperClass="col-span-6"
                                    loadingData={
                                      isFetchingInitialDevicesList ||
                                      isFetchingOrgVehicles ||
                                      isFetchingInitialDriversList ||
                                      isFetchingOrgDrivers ||
                                      isFetchingInitialGeozoneList ||
                                      isFetchingOrgGeozones ||
                                      isFetchingInitialGroupList ||
                                      isFetchingOrgGroups
                                    }
                                    label={t(fieldObject.param)}
                                    labelSubtext={
                                      [
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.DEVICE
                                          .field_name,
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.DRIVER
                                          .field_name,
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.GEOZONE
                                          .field_name,
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.GROUP
                                          .field_name,
                                      ].includes(fieldParam)
                                        ? t(
                                            `${fieldObject.param}_search_items_count`,
                                            {
                                              count:
                                                fieldParam ===
                                                APP_CONFIG.REPORTS
                                                  .BASIC_DROPDOWN_FILTERS.DEVICE
                                                  .field_name
                                                  ? dataInitialDevicesList?.count
                                                  : fieldParam ===
                                                      APP_CONFIG.REPORTS
                                                        .BASIC_DROPDOWN_FILTERS
                                                        .DRIVER.field_name
                                                    ? dataInitialDriversList?.count
                                                    : fieldParam ===
                                                        APP_CONFIG.REPORTS
                                                          .BASIC_DROPDOWN_FILTERS
                                                          .GEOZONE.field_name
                                                      ? dataInitialGeozoneList?.count
                                                      : fieldParam ===
                                                          APP_CONFIG.REPORTS
                                                            .BASIC_DROPDOWN_FILTERS
                                                            .GROUP.field_name
                                                        ? dataInitialGroupList?.count
                                                        : 0,
                                            }
                                          )
                                        : undefined
                                    }
                                    id={fieldObject.param}
                                    name={fieldObject.param}
                                    value={                                      
                                      fieldParam ===
                                        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS.GEOZONE.field_name
                                        ? isMulti && typeof fieldValue === "string"
                                          ? fieldValue
                                            .split(",")
                                            .map((option) => option.trim())
                                            .filter(Boolean)
                                            .map(getGeozoneOption)
                                          : Array.isArray(fieldValue)
                                            ? fieldValue.map((option) =>
                                              typeof option === "string"
                                                ? getGeozoneOption(option)
                                                : option.label
                                                  ? option
                                                  : {
                                                    ...option,
                                                    label: getGeozoneOption(
                                                      option.value
                                                    ).label,
                                                  }
                                            )
                                            : typeof fieldValue === "string"
                                              ? getGeozoneOption(fieldValue)
                                              : fieldValue
                                        : fieldValue
                                    }
                                    placeholder={t(
                                      `${fieldObject.param}_placeholder`
                                    )}
                                    isMulti={isMulti}
                                    additional={{ page: 1 }}
                                    defaultOption={
                                      // If it's the Device field and we have a value
                                      fieldObject.param ===
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.DEVICE
                                          .field_name &&
                                      !!values[fieldObject.param] &&
                                      !isMulti
                                        ? getAssetLabelObject(
                                            values[fieldObject.param] as string
                                          ) // Use helper to pass {value, label}
                                        : !!reportFilters?.[
                                              fieldObject.param
                                            ] &&
                                            !!values[fieldObject.param] &&
                                            values[fieldObject.param].length > 0
                                          ? typeof reportFilters?.[
                                              fieldObject.param
                                            ] === "object"
                                            ? reportFilters?.[fieldObject.param]
                                            : values[fieldObject.param]
                                          : undefined
                                    }
                                    // defaultOptions={[
                                    //   // default options for dropdown fields like Device ID, Driver ID, Zone ID, Group ID
                                    //   ...((fieldParam ===
                                    //   APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                                    //     .DEVICE.field_name // 'device_id'
                                    //     ? dataInitialDevicesList?.results?.map(
                                    //         (item: TLegacyVehicle) => ({
                                    //           value: `${item.deviceid}`,
                                    //           label:
                                    //             item.displayname || item.deviceid,
                                    //         })
                                    //       )
                                    //     : fieldParam ===
                                    //         APP_CONFIG.REPORTS
                                    //           .BASIC_DROPDOWN_FILTERS.DRIVER
                                    //           .field_name // 'driver_id'
                                    //       ? dataInitialDriversList?.results?.map(
                                    //           (item: TLegacyDriver) => ({
                                    //             value: `${item.driverid}`,
                                    //             label: driverDisplayName(item),
                                    //           })
                                    //         )
                                    //       : fieldParam ===
                                    //           APP_CONFIG.REPORTS
                                    //             .BASIC_DROPDOWN_FILTERS.GEOZONE
                                    //             .field_name // 'geozone_id'
                                    //         ? dataInitialGeozoneList?.results?.map(
                                    //             (item: TLegacyGeozone) => ({
                                    //               value: `${item.geozoneid}`,
                                    //               label: geozoneDisplayName(item),
                                    //             })
                                    //           )
                                    //         : fieldParam ===
                                    //             APP_CONFIG.REPORTS
                                    //               .BASIC_DROPDOWN_FILTERS.GROUP
                                    //               .field_name // 'group_id'
                                    //           ? dataInitialGroupList?.results?.map(
                                    //               (item: TLegacyGroup) => ({
                                    //                 value: `${item.groupid}`,
                                    //                 label: groupDisplayName(item),
                                    //               })
                                    //             )
                                    //           : []) || []),
                                    // ]}
                                    loadOptions={
                                      fieldParam ===
                                      APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                                        .DEVICE.field_name // 'device_id'
                                        ? debouncedLoadOptionsHandlerVehicle
                                        : fieldParam ===
                                            APP_CONFIG.REPORTS
                                              .BASIC_DROPDOWN_FILTERS.DRIVER
                                              .field_name // 'driver_id'
                                          ? debouncedLoadOptionsHandlerDriver
                                          : fieldParam ===
                                              APP_CONFIG.REPORTS
                                                .BASIC_DROPDOWN_FILTERS.GEOZONE
                                                .field_name // 'geozone_id'
                                            ? debouncedLoadOptionsHandlerGeozone
                                            : debouncedLoadOptionsHandlerGroup // 'group_id'
                                    }
                                    noOptionsMessage={() =>
                                      t(`${fieldObject.param}_no_item`)
                                    }
                                    onChange={(e) => {
                                      if (
                                        fieldParam ===
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.DEVICE
                                          .field_name
                                      ) {
                                        // 'device_id'
                                        // handleChangeDeviceId(e);
                                        setFieldValue(
                                          fieldParam,
                                          isMulti ? e : `${e?.value}`
                                        );
                                      } else if (
                                        fieldParam ===
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.DRIVER
                                          .field_name
                                      ) {
                                        // 'driver_id'
                                        // handleChangeDriverId(`${e?.value}`);
                                        setFieldValue(
                                          fieldParam,
                                          isMulti ? e : `${e?.value}`
                                        );
                                      } else if (
                                        fieldParam ===
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.GEOZONE
                                          .field_name
                                      ) {
                                        // 'geozone_id'
                                        // change callback for geozone should come here, if any
                                        setFieldValue(
                                          fieldParam,
                                          isMulti ? e: `${e?.value}`
                                        );
                                      } else if (
                                        fieldParam ===
                                        APP_CONFIG.REPORTS
                                          .BASIC_DROPDOWN_FILTERS.GROUP
                                          .field_name
                                      ) {
                                        // 'group_id'
                                        setFieldValue(
                                          fieldParam,
                                          isMulti ? e : `${e?.value}`
                                        );
                                      } else {
                                        setFieldValue(
                                          fieldParam,
                                          `${e?.value}`
                                        );
                                      }
                                    }}
                                    onBlur={(e) => {
                                      setFieldTouched(fieldObject.param, true);
                                      handleBlur(e);
                                    }}
                                    error={errors[fieldObject.param] as string}
                                    touched={
                                      touched[fieldObject.param] as boolean
                                    }
                                    detailsFormField={true}
                                  />
                                </>
                              )}
                            </>
                          )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Toggle for Calendar and Time Range fields */}
              {typeof values?.time_toggle !== "undefined" &&
                !!field_time_range &&
                !!field_start_time && (
                  <>
                    <AdminFormFieldCheckboxWithOnOffLabels
                      customWrapperClass="col-span-6 -mb-8"
                      label=""
                      offLabel={t("time_toggle_label_off")}
                      onLabel={t("time_toggle_label_on")}
                      id="time_toggle"
                      type="checkbox"
                      name="time_toggle"
                      checked={values.time_toggle}
                      onChange={(e) => {
                        setFieldValue(
                          "time_range",
                          e.target.checked
                            ? ""
                            : basicReportFilters?.find(
                                (item) => item.param === "time_range"
                              )?.value ||
                                APP_CONFIG.REPORTS.TIME_RANGE_OPTIONS[0].value
                        );
                        setTimeout(() => {
                          formik.validateForm();
                        }, 100);
                        handleChange?.(e);
                      }}
                      onBlur={handleBlur}
                      touched={touched.time_toggle as boolean}
                      error={errors.time_toggle as string}
                    />
                  </>
                )}

              {/* Time Range Dropdown */}
              {typeof values?.time_toggle !== "undefined" &&
                values.time_toggle === TIME_TOGGLE.RANGE &&
                !!field_time_range && (
                  <>
                    <AdminFormFieldDropdown
                      label={t(field_time_range.param)}
                      id={field_time_range.param}
                      name={field_time_range.param}
                      placeholder={t(`${field_time_range.param}_placeholder`)}
                      value={values[field_time_range.param]}
                      options={APP_CONFIG.REPORTS.TIME_RANGE_OPTIONS.map(
                        (item) => ({
                          ...item,
                          label: tReports(`label_time_range_${item.value}`),
                        })
                      )}
                      onChange={(e: any) => {
                        setFieldValue(field_time_range.param, e.value);
                      }}
                      onBlur={(e: any) => {
                        setFieldTouched(field_time_range.param, true);
                      }}
                      touched={touched[field_time_range.param] as boolean}
                      error={errors[field_time_range.param] as string}
                    />
                  </>
                )}

              {/* Start Date and Time */}
              {((typeof values?.time_toggle !== "undefined" &&
                values.time_toggle === TIME_TOGGLE.CALENDAR &&
                !!field_start_time) ||
                (typeof values?.time_toggle === "undefined" &&
                  !!field_start_time)) &&
                !!field_start_time &&
                !!values.timezone && ( // also ensure timezone is selected
                  <>
                    <AdminFormFieldInput
                      customWrapperClass="col-span-6"
                      enableSecondsSelect={true}
                      label={t(`${field_start_time.param}`)}
                      type="datetime-picker"
                      id={`${field_start_time.param}`}
                      name={`${field_start_time.param}`}
                      value={
                        !!values[`${field_start_time.param}`]
                          ? formattedDateTimeBasedOnTimezone(
                              dbTimeToJsTime(
                                parseInt(values[`${field_start_time.param}`])
                              ),
                              convertToTZForDateFns(values.timezone),
                              APP_CONFIG.DATETIME_FORMATS.DATEPICKER
                                .OUTPUT_DATE_TIME_SEC
                            )
                          : ""
                      }
                      maxTime={new Date()}
                      onDatepickerChange={handleDatetimePickerChange}
                      onBlur={handleBlur}
                      error={errors[`${field_start_time.param}`] as string}
                      touched={touched[`${field_start_time.param}`] as boolean}
                      detailsFormField={true}
                      size="lg"
                      placement={
                        basicReportFilters?.length > 3 ? "top" : "bottom"
                      }
                      renderCalendarPopupInBody={scrollingWithinModal}
                    />
                  </>
                )}

              {/* End Date and Time */}
              {((typeof values?.time_toggle !== "undefined" &&
                values.time_toggle === TIME_TOGGLE.CALENDAR &&
                !!field_start_time) ||
                (typeof values?.time_toggle === "undefined" &&
                  !!field_start_time)) &&
                !!field_end_time &&
                !!values.timezone && ( // also ensure timezone is selected
                  <>
                    <AdminFormFieldInput
                      customWrapperClass="col-span-6"
                      enableSecondsSelect={true}
                      label={t(`${field_end_time.param}`)}
                      type="datetime-picker"
                      id={`${field_end_time.param}`}
                      name={`${field_end_time.param}`}
                      value={
                        !!values[`${field_end_time.param}`]
                          ? formattedDateTimeBasedOnTimezone(
                              dbTimeToJsTime(
                                parseInt(values[`${field_end_time.param}`])
                              ),
                              convertToTZForDateFns(values.timezone),
                              APP_CONFIG.DATETIME_FORMATS.DATEPICKER
                                .OUTPUT_DATE_TIME_SEC
                            )
                          : ""
                      }
                      maxTime={new Date()}
                      onDatepickerChange={handleDatetimePickerChange}
                      onBlur={handleBlur}
                      error={errors[`${field_end_time.param}`] as string}
                      touched={touched[`${field_end_time.param}`] as boolean}
                      detailsFormField={true}
                      size="lg"
                      placement={
                        basicReportFilters?.length > 3 ? "top" : "bottom"
                      }
                      renderCalendarPopupInBody={scrollingWithinModal}
                    />
                  </>
                )}

              {(!!field_start_time || !!field_end_time) && (
                <>
                  {!showTimezoneDropdown && !!values.timezone ? (
                    <button
                      className="col-span-6 flex font-medium text-accent-blue-bright text-left"
                      onClick={() =>
                        setShowTimezoneDropdown(!showTimezoneDropdown)
                      }
                    >
                      {values.timezone}
                      {tzForDisplay === values.timezone
                        ? ` (${tMain("default")})`
                        : ""}
                      <img
                        alt="download"
                        src={svgIcons.ExpandMoreIcon}
                        className="size-7"
                      />
                    </button>
                  ) : (
                    <AdminFormFieldDropdown
                      label={t("timezone")}
                      id="timezone"
                      name="timezone"
                      placeholder={t("timezone_placeholder")}
                      value={!!values.timezone ? values.timezone : undefined}
                      options={TIMEZONE_OPTIONS}
                      onChange={(e: any) => {
                        setFieldValue("timezone", e.value);
                      }}
                      onBlur={(e: any) => {
                        setFieldTouched("timezone", true);
                      }}
                      touched={touched.timezone as boolean}
                      error={errors.timezone as string}
                      key={`timezone_${values.timezone}`}
                    />
                  )}
                </>
              )}

              {/* Additional Dropdown Fields */}
              {APP_CONFIG.REPORTS.ADDITIONAL_DROPDOWN_VALUES.filter((item) =>
                basicReportFilters?.find(
                  (filter) => filter.param === item.field_name
                )
              ).map((item) => {
                return (
                  <AdminFormFieldDropdown
                    key={item.field_name}
                    label={t(item.field_name)}
                    id={item.field_name}
                    name={item.field_name}
                    placeholder={t(`${item.field_name}_placeholder`)}
                    value={values[item.field_name]}
                    options={item.options}
                    onChange={(e: any) => {
                      setFieldValue(item.field_name, e.value);
                    }}
                    onBlur={(e: any) => {
                      setFieldTouched(item.field_name, true);
                    }}
                    touched={touched[item.field_name] as boolean}
                    error={errors[item.field_name] as string}
                    detailsFormField={true}
                  />
                );
              })}

              {/* Geozone Lookup */}
              {APP_CONFIG.REPORTS.GEOZONE_LOOKUP_LATITUDE_LONGITUDE.every(
                (item) =>
                  basicReportFilters?.some(
                    (filter) => filter.param === item.field_name
                  )
              ) && (
                <GAutocompleteField
                  onPlaceChanged={(place: google.maps.places.PlaceResult) => {
                    // set the field value
                    // formikSetValue("latitude", place?.formatted_address || "");
                    setFieldValue("address", place?.formatted_address || "");
                    setTimeout(() => {
                      setFieldTouched("address");
                    }, 200);
                    // place changed callback to move the geozone to the new location
                    onGAutocompletePlaceChanged(
                      place,
                      mapRef,
                      onGAutocompletePlaceChangeFinished
                    );
                  }}
                  onFieldClear={() => {
                    setFieldValue("address", "");
                    setFieldValue("geozone_lookup_add_lat", "");
                    setFieldValue("geozone_lookup_add_long", "");
                    setTimeout(() => {
                      setFieldTouched("address");
                    }, 200);
                  }}
                  fieldInAdminForm={true}
                  AdminFieldOptions={{
                    label: t("address"),
                    fieldName: "address",
                    values,
                    handleChange,
                    handleBlur,
                    errors,
                    touched,
                    userCanEdit: true,
                    setFieldError,
                  }}
                  onAddressNotFound={() => {
                    setFieldValue("geozone_lookup_add_lat", "");
                    setFieldValue("geozone_lookup_add_long", "");
                    setTimeout(() => {
                      setFieldTouched("address");
                    }, 200);
                  }}
                  wrapperClass="relative col-span-6 grid grid-cols-12"
                />
              )}

              {/* any other field */}
              {basicReportFilters?.map((item) => {
                if (
                  [
                    ...Object.keys(
                      APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                    ).map(
                      (filter) =>
                        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
                          filter as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                        ].field_name
                    ), // 'device_id', 'driver_id', 'geozone_id', 'group_id'
                    // excluding the known additional dropdown fields
                    ...APP_CONFIG.REPORTS.ADDITIONAL_DROPDOWN_VALUES.map(
                      (item) => item.field_name
                    ), // 'speed_over'

                    // excluding the geozone lookup fields
                    ...APP_CONFIG.REPORTS.GEOZONE_LOOKUP_LATITUDE_LONGITUDE.map(
                      (item) => item.field_name
                    ), // 'geozone_lookup_add_lat', 'geozone_lookup_add_long'
                    "start_time",
                    "end_time",
                    "timezone",
                    "time_range",
                    "ds_time_range", // dashboard specific time range
                    APP_CONFIG.REPORTS.GROUP_ASSETS_FILTER.field_name,
                  ].includes(item.param)
                )
                  return null;
                return (
                  <AdminFormFieldInput
                    key={item.id}
                    customWrapperClass="col-span-6"
                    label={t(item.param)}
                    id={item.param}
                    type="text"
                    name={item.param}
                    value={values[item.param]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors[item.param] as string}
                    touched={touched[item.param] as boolean}
                    detailsFormField={true}
                  />
                );
              })}
            </div>

            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <div className="absolute grid grid-cols-2 text-sm top-0 right-full bg-amber-600 text-white p-4 border border-gray-300 rounded w-[800px]">
                <span className="col-span-1">
                  <pre>
                    isFormValid: {isFormValid ? "true" : "false"}
                    <br />
                    dirty: {dirty ? "true" : "false"}
                    <br />
                    invalidFields: {invalidFields.join(", ")}
                    <br />
                    isSubmitting: {isSubmitting ? "true" : "false"}
                    <br />
                    isFetchingSpecificReport:{" "}
                    {isFetchingSpecificReport ? "true" : "false"}
                    <br />
                    isFetchingInitialDevicesList:{" "}
                    {isFetchingInitialDevicesList ? "true" : "false"}
                    <br />
                    isFetchingInitialDriversList:{" "}
                    {isFetchingInitialDriversList ? "true" : "false"}
                    <br />
                    isFetchingInitialGeozoneList:{" "}
                    {isFetchingInitialGeozoneList ? "true" : "false"}
                  </pre>
                  {errors && Object.keys(errors).length > 0 && (
                    <pre className="mb-4 text-red-200 font-bold mt-4">
                      errors: {JSON.stringify(errors, null, 2)}
                    </pre>
                  )}
                </span>
                <pre className="col-span-1">
                  values:
                  <br />
                  {JSON.stringify(values, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
        <div className={`grid grid-cols-6 gap-3 ${extraWrapperClass}`}>
          <AdminFormFieldSubmit
            label={customSubmitLabel || t("submit_button")}
            variant="primary"
            disabled={
              Object.keys(values).length == 0
                ? false // if no fields are present, allow submit
                : !isFormValid ||
                  isSubmitting ||
                  isFetchingSpecificReport ||
                  isFetchingInitialDevicesList ||
                  isFetchingInitialDriversList ||
                  isFetchingInitialGeozoneList
            }
          />

          {!!onCancel && (
            <AdminFormFieldSubmit
              label={tMain("cancel")}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting /* || isLoading*/}
              onClick={onCancel}
            />
          )}
        </div>
      </form>
    </>
  );
};
export default ReportOptions;
